import {uploadFiles} from '../../utils/uploadFiles';
import {
  BaseNodeFields,
  BranchNodeFormValues,
  MediaFieldValue,
  NetworkDetailsFormValues,
  NetworkNodeFormValues,
  RegulationFormValues,
  SubBranchNodeFormValues,
  UploadedMedia,
} from './components/NetworkDetails/types';
import {filterInvalidNodeArrays} from './components/NetworkDetails/networkDetailsSchema';

export function isUploadedMedia(value: unknown): value is UploadedMedia {
  return (
    typeof value === 'object' &&
    value !== null &&
    'url' in value &&
    typeof (value as UploadedMedia).url === 'string'
  );
}

export function getMediaFieldPreviewUrl(value: MediaFieldValue): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (isUploadedMedia(value)) return value.url;
  return null;
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp']);

export function inferUploadedMediaType(
  source: File | string,
): UploadedMedia['type'] {
  if (source instanceof File) {
    const mime = source.type || '';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  }

  const extension = source.split('.').pop()?.toLowerCase();
  if (extension && IMAGE_EXTENSIONS.has(extension)) return 'image';

  return 'document';
}


async function resolveSingleMediaField(
  value: MediaFieldValue,
): Promise<UploadedMedia | null> {
  if (!value) return null;

  if (value instanceof File) {
    const [uploaded] = await uploadFiles([value]);
    return uploaded ?? null;
  }

  return {url: value.url, type: value.type};
}

async function resolveMediaFilesArray(
  files: RegulationFormValues['files'] = [],
): Promise<UploadedMedia[]> {
  if (!Array.isArray(files)) return [];

  const existing: UploadedMedia[] = [];
  const newFiles: File[] = [];

  for (const file of files) {
    if (file instanceof File) {
      newFiles.push(file);
    } else if (isUploadedMedia(file)) {
      existing.push({url: file.url, type: file.type});
    }
  }

  const uploaded = newFiles.length > 0 ? await uploadFiles(newFiles) : [];
  return [...existing, ...uploaded];
}

async function transformBaseNodeFields<T extends BaseNodeFields>(
  node: T,
): Promise<T> {
  const [logo, mobileLogo, zoneDrawing, regulations] = await Promise.all([
    resolveSingleMediaField(node.logo),
    resolveSingleMediaField(node.mobileLogo),
    resolveSingleMediaField(node.zoneDrawing),
    Promise.all(
      (node.regulations ?? []).map(async regulation => ({
        ...regulation,
        files: await resolveMediaFilesArray(regulation.files),
      })),
    ),
  ]);

  return filterInvalidNodeArrays({
    ...node,
    logo,
    mobileLogo,
    zoneDrawing,
    regulations,
  });
}

async function transformSubBranchNode(
  node: SubBranchNodeFormValues,
): Promise<SubBranchNodeFormValues> {
  return transformBaseNodeFields(node);
}

async function transformBranchNode(
  node: BranchNodeFormValues,
): Promise<BranchNodeFormValues> {
  const transformed = await transformBaseNodeFields(node);
  const subBranches = await Promise.all(
    node.subBranches.map(transformSubBranchNode),
  );

  return {...transformed, subBranches};
}

async function transformNetworkNode(
  node: NetworkNodeFormValues,
): Promise<NetworkNodeFormValues> {
  const transformed = await transformBaseNodeFields(node);
  const branches = await Promise.all(node.branches.map(transformBranchNode));

  return {...transformed, branches};
}

export async function buildOnBoardingDraftPayload(
  network: NetworkDetailsFormValues['network'],
): Promise<NetworkDetailsFormValues['network']> {
  return Promise.all(network.map(transformNetworkNode));
}
